/**
 * Free gift with purchase.
 *
 * <e13-free-gift> (PDP buy box): the shopper adds the selected gift variant
 * with the card's own Add button; nothing is added automatically. The button
 * reflects cart state (Add / In Cart. Free) and re-syncs on every cartUpdate,
 * so removing the gift in the drawer restores the Add button. One gift per
 * order: the add is skipped while any variant of the gift is in the cart.
 *
 * <e13-free-gift-cart> (cart page): upgrades the plain /cart/add form to an
 * ajax add that re-renders the cart sections, mirroring cart.js.
 *
 * Pricing is a Buy X Get Y automatic discount in admin; this file never
 * touches prices.
 */
(function () {
  'use strict';

  const SOURCE = 'e13-free-gift';
  /** Gift product ids with an add in flight, so duplicate card instances
   *  (PDP + quick-add modal) cannot double-add. */
  const inflight = new Set();

  function cartAddConfig(body) {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    };
  }

  function cartAddUrl() {
    return `${window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add'}.js`;
  }

  /** Open the cart drawer when the theme has one, else go to the cart. */
  function openCart(event) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function' && !drawer.classList.contains('is-empty')) {
      event.preventDefault();
      drawer.open(event.currentTarget);
      return;
    }
    window.location.href = window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
  }

  function sectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : null;
  }

  /* Mirrors Dawn's CartItems pattern: look DOWN from the target for the
     selector, falling back to the target itself. Never walk up: closest()
     on '.shopify-section' from the cart icon is the whole header section,
     and replacing that wipes the header. */
  function replaceSection(id, selector, html) {
    if (!html) return;
    const target = document.getElementById(id);
    if (!target) return;
    const inner = target.querySelector(selector) || target;
    const replacement = sectionInnerHTML(html, selector);
    if (replacement !== null) inner.innerHTML = replacement;
  }

  if (!customElements.get('e13-free-gift')) {
    customElements.define(
      'e13-free-gift',
      class E13FreeGift extends HTMLElement {
        connectedCallback() {
          this.giftProductId = this.dataset.giftProductId;
          this.addBtn = this.querySelector('[data-fg-add]');

          if (this.addBtn) {
            this.addBtn.addEventListener('click', () => this.add());
          }

          // Swatch change: swap the thumbnail and the option label. Stop the
          // event here: ancestors (cart-items and friends) treat any change
          // as a quantity edit and error out on our radios.
          this.addEventListener('change', (event) => {
            const input = event.target;
            if (!input.classList || !input.classList.contains('free-gift__swatch-input')) return;
            event.stopPropagation();
            const image = this.querySelector('[data-fg-image]');
            if (image && input.dataset.image) image.src = input.dataset.image;
            const label = this.querySelector('[data-fg-option-value]');
            if (label && input.dataset.title) label.textContent = input.dataset.title;
          });

          const cartBtn = this.querySelector('[data-fg-cart]');
          if (cartBtn) cartBtn.addEventListener('click', openCart);

          this.syncState = this.syncState.bind(this);
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.syncState);
          }
          this.syncState();
        }

        disconnectedCallback() {
          if (this.unsubscribe) this.unsubscribe();
        }

        selectedVariantId() {
          const checked = this.querySelector('.free-gift__swatch-input:checked');
          return checked ? checked.value : null;
        }

        setAdded(count) {
          const added = count > 0;
          this.classList.toggle('free-gift--added', added);
          const addLabel = this.querySelector('[data-fg-add-label]');
          const addedLabel = this.querySelector('[data-fg-added-label]');
          if (addLabel) addLabel.hidden = added;
          if (addedLabel) addedLabel.hidden = !added;
          if (this.addBtn) this.addBtn.disabled = added;
          const cartBtn = this.querySelector('[data-fg-cart]');
          if (cartBtn) {
            cartBtn.hidden = !added;
            const countEl = cartBtn.querySelector('[data-fg-cart-count]');
            if (countEl) countEl.textContent = count;
            const label = `${count} in cart. View cart`;
            cartBtn.setAttribute('aria-label', label);
            cartBtn.title = label;
          }
        }

        /** Reflect how many of the gift product are in the cart. */
        syncState() {
          fetch(`${window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart'}.js`)
            .then((response) => (response.ok ? response.json() : null))
            .then((cart) => {
              if (!cart || !cart.items) return;
              let count = 0;
              cart.items.forEach((item) => {
                if (String(item.product_id) === String(this.giftProductId)) count += item.quantity;
              });
              this.setAdded(count);
            })
            .catch(() => {});
        }

        add() {
          const giftVariantId = this.selectedVariantId();
          if (!giftVariantId || inflight.has(this.giftProductId)) return;
          inflight.add(this.giftProductId);

          const spinner = this.addBtn && this.addBtn.querySelector('.loading-overlay__spinner');
          if (this.addBtn) this.addBtn.classList.add('loading');
          if (spinner) spinner.classList.remove('hidden');

          // With a cart drawer, re-render and open it so the gift is seen
          // landing at Free
          const drawer = document.querySelector('cart-drawer');
          // The drawer owns its render-target list (it grows on the cart page)
          const sections = drawer && typeof drawer.getSectionsToRender === 'function'
            ? drawer.getSectionsToRender().map((section) => section.id).join(',')
            : 'cart-icon-bubble';
          fetch(cartAddUrl(), cartAddConfig({ id: Number(giftVariantId), quantity: 1, sections: sections }))
            .then((response) => (response.ok ? response.json() : null))
            .then((added) => {
              if (!added) return;
              if (drawer && added.sections && added.sections['cart-drawer'] && typeof drawer.renderContents === 'function') {
                drawer.renderContents(added);
              } else if (added.sections && added.sections['cart-icon-bubble']) {
                replaceSection('cart-icon-bubble', '.shopify-section', added.sections['cart-icon-bubble']);
              }
              this.setAdded(1);
              if (typeof publish === 'function') {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, productVariantId: giftVariantId });
              }
            })
            .catch(() => {})
            .finally(() => {
              inflight.delete(this.giftProductId);
              if (this.addBtn) this.addBtn.classList.remove('loading');
              if (spinner) spinner.classList.add('hidden');
            });
        }
      }
    );
  }

  if (!customElements.get('e13-free-gift-cart')) {
    customElements.define(
      'e13-free-gift-cart',
      class E13FreeGiftCart extends HTMLElement {
        connectedCallback() {
          this.addLink = this.querySelector('[data-fg-add]');
          if (this.addLink) {
            this.addLink.addEventListener('click', (event) => {
              event.preventDefault();
              this.add();
            });
          }

          // Swatch change: swap the thumbnail and the option label. Stop the
          // event before it bubbles into cart-items / cart-drawer-items,
          // whose change handlers treat any change as a quantity edit and
          // surface "There was an error while updating your cart."
          this.addEventListener('change', (event) => {
            const input = event.target;
            if (!input.classList || !input.classList.contains('free-gift__swatch-input')) return;
            event.stopPropagation();
            const image = this.querySelector('[data-fg-image]');
            if (image && input.dataset.image) image.src = input.dataset.image;
            const label = this.querySelector('[data-fg-option-value]');
            if (label && input.dataset.title) label.textContent = input.dataset.title;
          });
        }

        selectedVariantId() {
          const checked = this.querySelector('.free-gift__swatch-input:checked');
          if (checked) return checked.value;
          // Single-variant gift: fall back to the permalink's id
          const match = this.addLink && this.addLink.href.match(/[?&]id=(\d+)/);
          return match ? match[1] : null;
        }

        /** The render-target list belongs to the host list component
         *  (cart-items / cart-drawer-items); read it live so the two never
         *  drift. */
        sectionsToRender() {
          const host = this.closest('cart-drawer-items') || this.closest('cart-items');
          if (host && typeof host.getSectionsToRender === 'function') return host.getSectionsToRender();
          return [{ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }];
        }

        add() {
          const variantId = this.selectedVariantId();
          if (!variantId || this.loading) return;
          this.loading = true;
          const button = this.addLink;
          const spinner = this.querySelector('.loading-overlay__spinner');
          if (button) button.classList.add('loading');
          if (spinner) spinner.classList.remove('hidden');

          const sections = this.sectionsToRender();
          fetch(
            cartAddUrl(),
            cartAddConfig({
              id: Number(variantId),
              quantity: 1,
              sections: sections.map((section) => section.section),
              sections_url: window.location.pathname,
            })
          )
            .then((response) => (response.ok ? response.json() : null))
            .then((added) => {
              if (!added || !added.sections) return;
              sections.forEach((section) => replaceSection(section.id, section.selector, added.sections[section.section]));
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS === 'object') {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, productVariantId: String(variantId) });
              }
            })
            .catch(() => {})
            .finally(() => {
              this.loading = false;
              // The row usually re-renders away with the section swap; reset
              // in case the add failed and it is still on the page.
              if (button && button.isConnected) button.classList.remove('loading');
              if (spinner && spinner.isConnected) spinner.classList.add('hidden');
            });
        }
      }
    );
  }
})();
